import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Helpers for keeping an admin list view (page, tab, search, sort, filters) in
 * the URL, so leaving the page and coming back restores what you were looking
 * at instead of resetting to the default view.
 *
 * The URL is read once on mount to seed state; from then on state is the source
 * of truth and gets written back. Values are validated on the way in, so a
 * stale or hand-edited URL falls back to defaults rather than reaching the API.
 */

// A filter is stored by name and is treated as unset when absent.
export const FILTER_UNSET = 'All';

export const readFilter = (params, key, unset = FILTER_UNSET) => params.get(key) || unset;

export const readPositiveInt = (params, key, fallback) => {
  const parsed = Number.parseInt(params.get(key), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const readOneOf = (params, key, allowed, fallback) => {
  const raw = params.get(key);
  return raw !== null && allowed.includes(raw) ? raw : fallback;
};

// Tabs travel as readable slugs ("creative") rather than indexes.
export const readTabIndex = (params, slugs, key = 'tab') => {
  const index = slugs.indexOf(params.get(key));
  return index === -1 ? 0 : index;
};

// A range only survives if both ends parse and stay inside the slider bounds.
export const readRange = (params, minKey, maxKey, [floor, ceiling]) => {
  const min = Number.parseInt(params.get(minKey), 10);
  const max = Number.parseInt(params.get(maxKey), 10);
  if (!Number.isInteger(min) || !Number.isInteger(max)) return [floor, ceiling];
  if (min < floor || max > ceiling || min > max) return [floor, ceiling];
  return [min, max];
};

/**
 * The query string as it was when the component mounted. Stable for the life of
 * the component, so it can seed `useState` initialisers without later URL
 * writes feeding back into them.
 */
export const useInitialSearchParams = () => {
  const { search } = useLocation();
  const initialParamsRef = useRef(null);
  if (initialParamsRef.current === null) {
    initialParamsRef.current = new URLSearchParams(search);
  }
  return initialParamsRef.current;
};

/**
 * Writes the current view into the URL, replacing rather than pushing so the
 * list keeps a single history entry. Opening a detail page then pushes on top
 * of it, which is what makes the browser's back button land on this view
 * unchanged.
 *
 * Pass an object of query key to value; entries that are null, undefined or an
 * empty string are left out, keeping the default view's URL clean. Callers
 * decide what counts as a default, e.g. `{ page: page !== 1 ? page : null }`.
 */
export const useSyncViewToUrl = values => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    params.set(key, String(value));
  });
  const next = params.toString();

  useEffect(() => {
    // Depending on the serialised string keeps this to one run per real change,
    // and the comparison makes re-running it (StrictMode) a no-op.
    if (next === new URLSearchParams(search).toString()) return;
    navigate({ pathname, search: next ? `?${next}` : '' }, { replace: true });
  }, [next, pathname, search, navigate]);
};

/**
 * True when this page was opened directly rather than navigated to from within
 * the app, meaning there is no history entry to step back to.
 */
export const isFirstHistoryEntry = location => location.key === 'default';
